import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { X, LogIn, LogOut, Crown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { navCategories } from './navConfig';

// Mobile hamburger drawer. Slides in from the left with full categorized menu.
// Footer shows total consultas + app version. Auto-closes on route change.
const MobileDrawer = ({ open, onClose, badges = {}, consultasTotal = null }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isPro } = useAuth();

  // Close on route change (path only — do NOT depend on `open` or it'd close on open)
  const closeRef = React.useRef(onClose);
  closeRef.current = onClose;
  const openRef = React.useRef(open);
  openRef.current = open;
  useEffect(() => {
    if (openRef.current) closeRef.current?.();
  }, [location.pathname]);

  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const resolvePath = (item) => (item.authAware && !user ? '/auth' : item.path);

  const handleLogout = async () => { await logout(); onClose?.(); navigate('/'); };

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 bg-black/50 z-[60] lg:hidden transition-opacity duration-300',
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        )}
        onClick={onClose}
        data-testid="drawer-backdrop"
        aria-hidden={!open}
      />

      {/* Drawer panel */}
      <aside
        className={cn(
          'fixed top-0 left-0 bottom-0 w-[86%] max-w-[320px] bg-white z-[70] lg:hidden',
          'flex flex-col shadow-2xl safe-area-top safe-area-bottom',
          'transition-transform duration-300 ease-out will-change-transform',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Menu de navegação"
        data-testid="mobile-drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E2E8F0]">
          <Link to="/" onClick={onClose} className="flex items-center gap-2" data-testid="drawer-logo">
            <img src="/logo.png" alt="Tô de Olho" className="h-8 w-8 rounded-md" />
            <span className="text-sm font-black text-[#006030]">Tô de Olho</span>
          </Link>
          <button
            type="button"
            onClick={onClose}
            className="p-2 -mr-2 rounded-md text-[#475569] hover:bg-[#F4F6F8]"
            data-testid="drawer-close-btn"
            aria-label="Fechar menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User strip */}
        <div className="px-4 py-3 border-b border-[#E2E8F0] bg-[#F9FAFB]">
          {user ? (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#006030] text-white flex items-center justify-center font-bold">
                {(user.nome || user.email || '?').slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#0F1C2E] truncate" data-testid="drawer-user-name">
                  {user.nome || user.email}
                </p>
                <div className="flex items-center gap-1 text-[11px] text-[#475569]">
                  {isPro && <Crown className="w-3 h-3 text-[#F0D000]" />}
                  <span>{isPro ? 'Plano PRO' : 'Plano Gratuito'}</span>
                </div>
              </div>
            </div>
          ) : (
            <button
              onClick={() => { onClose?.(); navigate('/auth'); }}
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#006030] hover:bg-[#004020] text-white text-sm font-semibold rounded-md transition-colors"
              data-testid="drawer-login-btn"
            >
              <LogIn className="w-4 h-4" /> Entrar / Criar conta
            </button>
          )}
        </div>

        {/* Categories */}
        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-4 custom-scrollbar">
          {navCategories.map((cat) => (
            <div key={cat.title}>
              <div className="px-2 mb-1 text-[10px] font-bold tracking-wider text-[#94A3B8]">
                {cat.title}
              </div>
              <div className="space-y-0.5">
                {cat.items.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  const badge = item.badgeKey ? badges[item.badgeKey] || 0 : 0;
                  return (
                    <Link
                      key={item.path + item.label}
                      to={resolvePath(item)}
                      data-testid={`drawer-${item.testId}`}
                      className={cn(
                        'flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors relative',
                        active
                          ? 'bg-[#E8F5E9] text-[#006030] font-semibold'
                          : 'text-[#334155] hover:bg-[#F4F6F8] hover:text-[#006030]',
                      )}
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      <span className="truncate">{item.label}</span>
                      {badge > 0 && (
                        <span className="ml-auto min-w-[18px] h-4 flex items-center justify-center rounded-full bg-[#C62828] text-white text-[10px] font-bold px-1.5">
                          {badge > 99 ? '99+' : badge}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}

          {user && (
            <div className="pt-2 border-t border-[#E2E8F0]">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-[#C62828] hover:bg-[#FEE2E2] transition-colors"
                data-testid="drawer-logout-btn"
              >
                <LogOut className="w-4 h-4" /> Sair da conta
              </button>
            </div>
          )}
        </nav>

        {/* Footer */}
        <div className="border-t border-[#E2E8F0] px-4 py-3 bg-[#F9FAFB]">
          {consultasTotal !== null && consultasTotal > 0 && (
            <p className="text-[11px] text-[#475569] mb-1" data-testid="drawer-consultas-total">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#006030] mr-1.5 align-middle" />
              {consultasTotal.toLocaleString('pt-BR')} consultas realizadas
            </p>
          )}
          <div className="flex justify-between items-center text-[10px] text-[#94A3B8]">
            <div className="flex gap-2">
              <Link to="/termos" className="hover:text-[#006030]">Termos</Link>
              <Link to="/privacidade" className="hover:text-[#006030]">Privacidade</Link>
            </div>
            <span data-testid="drawer-app-version">Tô de Olho v1.0</span>
          </div>
        </div>
      </aside>
    </>
  );
};

export default MobileDrawer;
